import { trainingProducts } from '../data/trainingProducts'
import { courseTabs, courseVenues } from '../data/trainingCourseOptions'
import {
  CourseStatus,
  CourseTeacher,
  CourseVenue,
  TrainingCourse,
  TrainingCourseDraft,
} from '../types/trainingCourse'

export function getTrainingProduct(code: string) {
  return trainingProducts.find((p) => p.code === code)
}

export function productLabel(code: string): string {
  const product = getTrainingProduct(code)
  return product
    ? `${product.type} — ${product.name} · ${product.code}`
    : code || '-'
}

export function venueLabel(venue?: CourseVenue): string {
  if (!venue) return ''
  return courseVenues.find((v) => v.value === venue)?.label || venue
}

export function commissionLabel(course: TrainingCourse): string {
  if (!course.commission) return ''
  return course.commission === 'SEFORM'
    ? 'SEFORM'
    : course.clientName || 'Cliente'
}

export function parseCourseStatus(value: string | null): CourseStatus {
  return courseTabs.some((t) => t.id === value)
    ? (value as CourseStatus)
    : 'attivi'
}

export function emptyCourseDraft(status: CourseStatus): TrainingCourseDraft {
  const draft: TrainingCourseDraft = {
    name: '',
    productCode: '',
    courseCode: '',
    externalProduct: false,
    characteristic: 'Unico',
    commission: 'SEFORM',
    venue: 'seform',
    status,
    teachers: [],
  }
  return normalizeCourse(draft)
}

// Strips the fields that do not apply to on-demand courses
export function normalizeCourse<T extends TrainingCourseDraft>(course: T): T {
  const withoutExternal = course.externalProduct
    ? course
    : { ...course, externalCostAmount: undefined, externalSupplier: undefined }
  if (course.status !== 'ondemand') return withoutExternal
  return {
    ...withoutExternal,
    commission: undefined,
    clientName: undefined,
    venue: undefined,
    clientSite: undefined,
  }
}

export function getTrainedCourses(
  courses: TrainingCourse[],
  leadId?: string,
): TrainingCourse[] {
  if (!leadId) return []
  return courses.filter(
    (c) => c.characteristic === 'Trainato' && c.leadCourseId === leadId,
  )
}

export function teacherLineTotal(teacher: CourseTeacher): number {
  return (teacher.hourlyRate || 0) * (teacher.hours || 0)
}

export function teachingTotal(course: TrainingCourseDraft): number {
  return (course.teachers || []).reduce((s, t) => s + teacherLineTotal(t), 0)
}

export function teachingHours(course: TrainingCourseDraft): number {
  return (course.teachers || []).reduce((s, t) => s + (t.hours || 0), 0)
}

export function externalProductCost(course: TrainingCourseDraft): number {
  return course.externalProduct ? course.externalCostAmount || 0 : 0
}

export function courseTotalCost(course: TrainingCourseDraft): number {
  return (
    teachingTotal(course) +
    (course.equipmentAmount || 0) +
    (course.roomRentalAmount || 0) +
    (course.otherCostsAmount || 0) +
    externalProductCost(course)
  )
}

export function formatCourseDate(iso?: string): string {
  if (!iso) return ''
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}
